import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ItemSearchSelectFilterModule, SelectedDropdownItem } from '@galaxy/inventory-ui';
import { App, Edition } from '@galaxy/marketplace-apps';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeactivationType as AccountDeactivationType } from '@vendasta/accounts/legacy';
import { RuleInterface } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { ItemType, ListActivatableItemsFilters } from '@vendasta/marketplace-apps';
import { AddonInterface } from '@vendasta/marketplace-packages';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { ANY_ADDON, ANY_EDITION, AppsService } from '../../../../../data-services/apps.service';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { ALL_PRODUCTS, ANY, EMPTY_PLACEHOLDER, NO_SELECTION } from '../constants';

const DeactivationType = 'deactivation_type';
const AppId = 'app_id';
const EditionId = 'edition_id';
const AddonId = 'addon_id';
const IncludeTrials = 'include_trials';

@Component({
  selector: 'automata-deactivate-product-panel',
  templateUrl: './deactivate-product-panel.component.html',
  styleUrls: ['./deactivate-product-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    ItemSearchSelectFilterModule,
    MatCheckboxModule,
  ],
})
export class DeactivateProductPanelComponent extends CommonActionPanelComponent implements OnInit, OnDestroy {
  ALL_PRODUCTS = ALL_PRODUCTS;
  ANY_EDITION = ANY_EDITION;
  ANY_ADDON = ANY_ADDON;
  deactivationWarning: string;
  deactivationOptions: SelectInputOptionInterface[] = [
    {
      label: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.DEACTIVATION_TYPES.CANCEL'),
      value: AccountDeactivationType.DEACTIVATION_TYPE_CANCEL,
    },
    {
      label: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.DEACTIVATION_TYPES.DEACTIVATE'),
      value: AccountDeactivationType.DEACTIVATION_TYPE_IMMEDIATE,
    },
  ];
  deactivationHint$: Observable<string>;
  deactivationHints = new Map<AccountDeactivationType, string>([
    [AccountDeactivationType.DEACTIVATION_TYPE_CANCEL, 'AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.HINT.CANCEL'],
    [
      AccountDeactivationType.DEACTIVATION_TYPE_IMMEDIATE,
      'AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.HINT.DEACTIVATE',
    ],
  ]);
  apps$: Observable<App[]>;
  deactivationTypeControl: UntypedFormControl = new UntypedFormControl(0, Validators.required);
  previousProduct: string;
  productControl: UntypedFormControl = new UntypedFormControl(NO_SELECTION, []);
  previousEditionOrAddon: string;
  editionsOrAddonsControl: UntypedFormControl = new UntypedFormControl(NO_SELECTION, []);
  addonControl: UntypedFormControl = new UntypedFormControl(NO_SELECTION, []);
  editionControl: UntypedFormControl = new UntypedFormControl(NO_SELECTION, []);
  trialControl: UntypedFormControl = new UntypedFormControl(false);
  isLoading = true;
  appOptions$: Observable<SelectInputOptionInterface[]>;
  disableEditionSelection = false;
  editionOptions$: Observable<SelectInputOptionInterface[]>;
  disableAddonSelection = false;
  addonOptions$: Observable<SelectInputOptionInterface[]>;
  editionsOrAddonsOptions: SelectInputOptionInterface[];

  listActivatableItemsFilterForAppType = new ListActivatableItemsFilters({
    itemTypes: [ItemType.ITEM_TYPE_APP],
  });
  listActivatableItemsFilterForAddonType$: Observable<ListActivatableItemsFilters>;

  private selectedAppId$$ = new BehaviorSubject('');
  public readonly selectedAppId$ = this.selectedAppId$$.asObservable();
  private selectedAddonId$$ = new BehaviorSubject('');
  public readonly selectedAddonId$ = this.selectedAddonId$$.asObservable();

  destroyed$$ = new Subject<void>();

  disableProductSelection$ = this.automationService.isNonPartnerAutomation().pipe(
    take(1),
    map((isNonPartnerAutomation) => {
      if (isNonPartnerAutomation) {
        this.productControl.disable();
        return true;
      }
      this.productControl.setValidators(Validators.required);
      return false;
    }),
  );

  constructor(
    protected readonly automationService: AutomationViewService,
    protected readonly appsService: AppsService,
    protected readonly translateService: TranslateService,
    private readonly ref: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();

    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    const initialAppSelected$ = this.automationService.isBuiltInAutomationView().pipe(
      map((isBuiltInAutomationView) => {
        if (isBuiltInAutomationView) {
          this.previousProduct = ALL_PRODUCTS;
          return ALL_PRODUCTS;
        }
        this.previousProduct = jsonData[AppId] || '';
        return jsonData[AppId] || '';
      }),
    );

    const initialDeactivationType = jsonData[DeactivationType] || AccountDeactivationType.DEACTIVATION_TYPE_CANCEL;
    // Default to the top selection of either edition or add-on (where EditionId === <ANY-EDITION> && AddonId === *)
    let initialEditionsOrAddonsSelected = ANY;
    // check to see if addon is selected by validating it doesn't have the values its assigned when it isn't selected
    if (
      jsonData[AddonId] !== NO_SELECTION &&
      jsonData[AddonId] !== ANY &&
      jsonData[AddonId] !== ANY_EDITION &&
      jsonData[EditionId] === ANY_EDITION
    ) {
      initialEditionsOrAddonsSelected = ANY_ADDON;
      // if its not any of the above values then we check to see if its the value assigned to addons when an edition is selected ("")
    } else if (jsonData[AddonId] === NO_SELECTION) {
      initialEditionsOrAddonsSelected = ANY_EDITION;
    }
    this.previousEditionOrAddon = initialEditionsOrAddonsSelected;
    let initialAddonSelected = NO_SELECTION;
    if (jsonData[AddonId] !== ANY && jsonData[AddonId] !== ANY_EDITION) {
      initialAddonSelected = jsonData[AddonId];
    }
    let initialEditionSelected = jsonData[EditionId] === NO_SELECTION ? EMPTY_PLACEHOLDER : jsonData[EditionId];
    initialEditionSelected = initialEditionSelected || ANY_EDITION;
    const initialIncludeTrials = jsonData[IncludeTrials] || false;

    this.deactivationTypeControl.setValue(initialDeactivationType);
    this.editionsOrAddonsControl.setValue(initialEditionsOrAddonsSelected);
    this.editionControl.setValue(initialEditionSelected);
    this.addonControl.setValue(jsonData[AddonId] || ANY_ADDON);
    this.trialControl.setValue(initialIncludeTrials);
    initialDeactivationType === AccountDeactivationType.DEACTIVATION_TYPE_CANCEL
      ? (this.deactivationWarning = this.translateService.instant(
          'AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.WARNING.CANCEL_WARNING',
        ))
      : (this.deactivationWarning = this.translateService.instant(
          'AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.WARNING.DEACTIVATE_WARNING',
        ));

    this.apps$ = this.disableProductSelection$.pipe(
      switchMap((disableProductSelection) => {
        // If product selection is disabled, don't load any options.
        if (disableProductSelection) {
          return of([]);
        }

        // Otherwise get all apps available to the account.
        return this.appsService.getApps();
      }),
      tap(() => (this.isLoading = false)),
      take(1),
      shareReplay(1),
    );

    // Derive the options available to each app.
    const appOptionsStaged$ = this.apps$.pipe(
      // Load the normal apps.
      map((apps) => apps.map(this.mapAppToOption)),

      // Add the special selections (e.g. all products)
      map((options) => [
        {
          label: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.ALL_PRODUCTS'),
          value: ALL_PRODUCTS,
          disabled: false,
          selected: false,
        },
        ...options,
      ]),
    );

    this.appOptions$ = combineLatest([initialAppSelected$, appOptionsStaged$]).pipe(
      // Show the selected one in the UI.
      map(([initialAppSelected, options]) => {
        return options.map((option) => {
          if (option.value === initialAppSelected) {
            option.selected = true;
          }
          return option;
        });
      }),

      // Once the options for the products to deactivate is present, saving can occur.
      tap(() => (this.savingEnabled = true)),
      take(1),
    );

    // Set the initial selected app.
    initialAppSelected$
      .pipe(
        map((initialAppSelected) => {
          if (!initialAppSelected) {
            initialAppSelected = ALL_PRODUCTS;
          }
          this.productControl.setValue(initialAppSelected);
          this.selectedAppId$$.next(initialAppSelected);
          if (initialAppSelected !== ANY || initialAddonSelected !== NO_SELECTION) {
            this.setAddonOptions(initialAddonSelected);
          }
        }),
        take(1),
      )
      .subscribe();

    this.editionsOrAddonsOptions = [
      {
        value: ANY,
        label: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.EDITION_AND_ADDON'),
      },
      {
        value: ANY_EDITION,
        label: this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_EDITION'),
      },
      {
        value: ANY_ADDON,
        label: this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_ADDON'),
      },
    ];

    this.editionOptions$ = combineLatest([this.selectedAppId$, this.apps$]).pipe(
      map(([appId, apps]) => {
        const app = apps.find((a) => a.key.appId === appId);
        const options = app?.editionInformation?.editions?.map(this.mapEditionToOption) || [];
        if (options.length > 0) {
          options.unshift({
            label: 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ANY_EDITION',
            value: ANY_EDITION,
          });
          this.disableEditionSelection = false;
          this.editionControl.enable();
        } else if (app) {
          options.push({
            label: app.sharedMarketingInformation.name,
            value: ANY_EDITION,
          });
          this.disableEditionSelection = true;
          this.editionControl.disable();
        }
        return options;
      }),
      map((options) => {
        return this.setSelectedEditionOption(initialEditionSelected, options);
      }),
    );

    this.deactivationOptions.forEach((option) => {
      if (option.value === initialDeactivationType) {
        option.selected = true;
      }
    });

    if (this.deactivationTypeControl.value) {
      this.deactivationHint$ = this.translateService.stream(
        this.deactivationHints.get(this.deactivationTypeControl.value),
      );
    }
    this.deactivationTypeControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$$),
        tap((value) => {
          this.deactivationHint$ = this.translateService.stream(this.deactivationHints.get(value));
          value === AccountDeactivationType.DEACTIVATION_TYPE_CANCEL
            ? (this.deactivationWarning = this.translateService.instant(
                'AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.WARNING.CANCEL_WARNING',
              ))
            : (this.deactivationWarning = this.translateService.instant(
                'AUTOMATIONS.EDITOR.TASKS.DEACTIVATE_PRODUCT.WARNING.DEACTIVATE_WARNING',
              ));
        }),
      )
      .subscribe();

    this.editionsOrAddonsControl.setValue(initialEditionsOrAddonsSelected);
    this.editionControl.setValue(initialEditionSelected);
    this.trialControl.setValue(initialIncludeTrials);
    this.addonControl.setValue(initialAddonSelected);
    this.selectedAddonId$$.next(initialAddonSelected);

    this.productControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroyed$$),
        tap((value) => {
          this.selectedAppId$$.next(value);
          if (value !== this.previousProduct) {
            this.previousProduct = value;
            if (value !== ANY && value !== NO_SELECTION) {
              this.setAddonOptions(initialAddonSelected);
              this.editionsOrAddonsControl.setValue(ANY);
            }
          }
        }),
      )
      .subscribe();

    this.editionsOrAddonsControl.valueChanges
      .pipe(
        tap((val) => {
          if (val !== this.previousEditionOrAddon) {
            this.previousEditionOrAddon = val;
            if (val === ANY_EDITION) {
              this.editionControl.setValue(ANY_EDITION);
              this.trialControl.setValue(false);
              this.addonControl.setValue(NO_SELECTION);
            } else if (val === ANY_ADDON) {
              this.editionControl.setValue(NO_SELECTION);
              this.trialControl.setValue(false);
              this.addonControl.setValue(ANY_ADDON);
            } else {
              this.editionControl.setValue(NO_SELECTION);
              this.trialControl.setValue(false);
              this.addonControl.setValue(NO_SELECTION);
            }
          }
        }),
        takeUntil(this.destroyed$$),
      )
      .subscribe();

    this.addonControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$$),
        tap((value) => this.selectedAddonId$$.next(value)),
      )
      .subscribe();

    this.ref.detectChanges();

    this.listActivatableItemsFilterForAddonType$ = this.selectedAppId$.pipe(
      map(
        (selectedAppId) =>
          new ListActivatableItemsFilters({
            requiredItemId: selectedAppId,
            itemTypes: [ItemType.ITEM_TYPE_ADDON],
          }),
      ),
    );
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
  }

  private mapAppToOption(app: App): SelectInputOptionInterface {
    return {
      label: app.sharedMarketingInformation?.name,
      value: app.key?.appId,
    };
  }

  private mapEditionToOption(edition: Edition): SelectInputOptionInterface {
    return {
      label: edition.name,
      value: edition.appKey?.editionId || EMPTY_PLACEHOLDER,
    };
  }

  private mapAddonToOption(addons: AddonInterface): SelectInputOptionInterface {
    return {
      label: addons.title,
      value: addons.addonId ? addons.addonId : EMPTY_PLACEHOLDER,
    };
  }

  setEditionOrAddonOptions(): void {
    if (this.disableAddonSelection) {
      this.editionsOrAddonsOptions[2].label = this.translateService.instant(
        'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.NO_ADDONS',
      );
    } else {
      this.editionsOrAddonsOptions[2].label = this.translateService.instant(
        'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.PRODUCT_ADDON',
      );
    }
  }

  setAddonOptions(initialAddonSelected): void {
    this.addonOptions$ = this.selectedAppId$.pipe(
      switchMap((appId) => this.appsService.getAddons(appId)),
      map((a) => {
        let options = [];
        if (a) {
          options = a.map(this.mapAddonToOption);
          if (options.length > 0) {
            options.unshift({
              label: 'AUTOMATIONS.EDITOR.TRIGGERS.PRODUCT_ACTIVATION.ALL_ADDONS',
              value: ANY_ADDON,
            });
            this.disableAddonSelection = false;
          }
        }
        if (options.length === 0) {
          this.disableAddonSelection = true;
        }
        this.setEditionOrAddonOptions();
        return options;
      }),
      map((options) => {
        return this.setSelectedAddonOption(initialAddonSelected, options);
      }),
    );
  }

  setSelectedEditionOption(
    initialEditionSelected: string,
    options: SelectInputOptionInterface[],
  ): SelectInputOptionInterface[] {
    return options.map((option) => {
      if (
        option.value === initialEditionSelected ||
        (option.value === EMPTY_PLACEHOLDER && initialEditionSelected === NO_SELECTION)
      ) {
        option.selected = true;
      }
      return option;
    });
  }

  setSelectedAddonOption(
    initialAddonSelected: string,
    options: SelectInputOptionInterface[],
  ): SelectInputOptionInterface[] {
    return options.map((option) => {
      if (
        option.value === initialAddonSelected ||
        (option.value === EMPTY_PLACEHOLDER && initialAddonSelected === NO_SELECTION)
      ) {
        option.selected = true;
      }
      return option;
    });
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    jsonData[DeactivationType] = this.deactivationTypeControl.value;
    jsonData[AppId] = this.productControl.value;
    if (this.editionsOrAddonsControl.value === ANY_EDITION && this.disableEditionSelection) {
      jsonData[EditionId] = ANY_EDITION;
      jsonData[AddonId] = ANY_EDITION;
    } else if (this.editionsOrAddonsControl.value === ANY_EDITION) {
      jsonData[EditionId] = this.editionControl.value === EMPTY_PLACEHOLDER ? '' : this.editionControl.value;
      jsonData[AddonId] = NO_SELECTION;
    } else if (this.editionsOrAddonsControl.value === ANY_ADDON) {
      jsonData[EditionId] = ANY_EDITION;
      jsonData[AddonId] = this.addonControl.value === EMPTY_PLACEHOLDER ? '' : this.addonControl.value;
    } else {
      jsonData[EditionId] = ANY_EDITION;
      jsonData[AddonId] = ANY;
    }
    jsonData[IncludeTrials] = this.trialControl.value;

    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.productControl];
  }

  productSelectionChanged(selectedDropdownItem: SelectedDropdownItem): void {
    const appId = selectedDropdownItem ? selectedDropdownItem.appId : '';
    this.productControl.setValue(appId);
    this.selectedAppId$$.next(appId);
  }

  addonSelectionChanged(selectedDropdownItem: SelectedDropdownItem): void {
    const addonId = selectedDropdownItem ? selectedDropdownItem.addonId : '';
    this.addonControl.setValue(addonId);
    this.selectedAddonId$$.next(addonId);
  }

  isFormValid(): boolean {
    return !this.productControl.invalid;
  }
}
